import React from "react";
import { Button } from "@/components/ui/button";
import { RefreshCw } from "lucide-react";

type Props = {
  children: React.ReactNode;
  fallback?: React.ReactNode;
};

type State = {
  hasError: boolean;
  message?: string;
};

export class ErrorBoundary extends React.Component<Props, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(error: unknown): State {
    const message = error instanceof Error ? error.message : String(error);
    return { hasError: true, message };
  }

  componentDidCatch(error: unknown, info: unknown) {
    // Keep logs concise but helpful in native WebView
    console.error("[ErrorBoundary] Uncaught render error:", error, info);
  }

  handleRetry = () => {
    this.setState({ hasError: false, message: undefined });
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }
      
      return (
        <div className="min-h-screen bg-background flex items-center justify-center p-6">
          <div className="max-w-md text-center space-y-4">
            <p className="text-destructive font-medium">Erreur d'affichage</p>
            <p className="text-sm text-muted-foreground">
              {this.state.message || "Une erreur est survenue."}
            </p>
            <Button
              variant="outline"
              onClick={this.handleRetry}
              className="gap-2"
            >
              <RefreshCw className="w-4 h-4" />
              Réessayer
            </Button>
            <p className="text-xs text-muted-foreground">
              Si le problème persiste, ferme et relance l'app.
            </p>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
